import { Delete, Edit } from "@mui/icons-material"
import { Typography, styled } from "@mui/material"
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid-pro"
import type { CriticalThreatWithReferences } from "@phc/common"
import { AssetType, IndustryType } from "@phc/common/gen/preferencesGen"
import { getLocationId } from "../../utils/helpers"
import {
  assetTypeEnumToString,
  industryTypeEnumToString,
} from "../../utils/helpers/assetHelper"
import type { Asset } from "../../utils/preferencesApi"
import { extraColors } from "../../utils/theme"
import { CriticalThreatTag } from "../CriticalThreats/CriticalThreatTag"
import { getFirstRelatedCriticalThreat } from "../CriticalThreats/criticalThreatHelper"

const SmallCell = styled(Typography)(({ theme }) => ({
  ...theme.typography.small1,
}))

export const locationColumnInfo = (
  setEditRow: (val?: Asset) => void,
  setAssetModalOpen: (val: boolean) => void,
  setRemoveAssetId: (val: string[]) => void,
  isLocationSelector: boolean,
  criticalThreats?: CriticalThreatWithReferences[]
) => {
  let selectorColumns: GridColDef<Asset>[] = [
    {
      field: "name" as const,
      headerName: "Location",
      minWidth: 100,
      flex: 1,
      valueGetter: (_, row) => {
        return row.name
      },
    },

    {
      field: "address" as const,
      headerName: "Address",
      minWidth: 150,
      flex: 1,
      display: "flex",
      valueGetter: (_, row) => {
        return row.base_event?.mapbox_location?.place_name
      },
      renderCell: ({ value }: { value?: string }) => {
        return <SmallCell>{value}</SmallCell>
      },
    },
    {
      field: "group" as const,
      headerName: "Group",
      minWidth: 136,
      maxWidth: 220,
      display: "flex",
      flex: 1,
      valueGetter: (_, row) => {
        return (row.asset_groups ?? []).map(g => g.name).join(", ")
      },
      renderCell: ({ value }) => {
        return <SmallCell>{value}</SmallCell>
      },
    },
    {
      field: "industry" as const,
      headerName: "Industry",
      display: "flex",
      valueGetter: (_, row) => {
        return row.industry &&
          row.industry !== IndustryType.INDUSTRY_TYPE_UNSPECIFIED
          ? industryTypeEnumToString(row.industry)
          : ""
      },
      renderCell: ({ value }) => {
        return <SmallCell>{value}</SmallCell>
      },
    },
    {
      field: "assetType" as const,
      headerName: "Asset Type",
      display: "flex",
      valueGetter: (_, row) => {
        return (row.asset_types ?? [])
          .filter(a => a !== AssetType.ASSET_TYPE_UNSPECIFIED)
          .map(a => assetTypeEnumToString(a))
          .join(", ")
      },
      renderCell: ({ value }) => {
        return <SmallCell>{value}</SmallCell>
      },
    },
  ]

  if (criticalThreats?.length) {
    selectorColumns = selectorColumns.concat([
      {
        field: "criticalThreat" as const,
        headerName: "Critical Threat",
        minWidth: 125,
        maxWidth: 125,
        display: "flex",
        align: "center",
        valueGetter: (_, row) => {
          const hasCriticalThreat = getFirstRelatedCriticalThreat(
            getLocationId(row) || "",
            criticalThreats
          )

          // value must be string for filtering
          return hasCriticalThreat ? "true" : "false"
        },
        renderCell: ({ row }) => {
          const relatedCriticalThreat = getFirstRelatedCriticalThreat(
            getLocationId(row) || "",
            criticalThreats
          )

          return relatedCriticalThreat ? (
            <CriticalThreatTag relatedCriticalThreat={relatedCriticalThreat} />
          ) : null
        },
      },
    ])
  }

  if (isLocationSelector) return selectorColumns

  return selectorColumns.concat([
    // TODO: Add back once we want to display threat radius again
    // {
    //   field: "threat_radius" as const,
    //   headerName: "Threat Radius",
    //   flex: 1,
    //   renderCell: ({ formattedValue }) => {
    //     return (
    //       <Typography
    //         variant="small1"
    //         sx={{
    //           border: "1px solid",
    //           borderColor: extraColors.light,
    //           borderRadius: 2,
    //           padding: "4px",
    //         }}
    //       >
    //         {formattedValue}
    //       </Typography>
    //     )
    //   },
    //   valueFormatter: ({ value }) => {
    //     return value + " miles"
    //   },
    // } satisfies GridColDef<Asset, string>,
    {
      field: "actions" as const,
      type: "actions",
      width: 50,
      getActions: ({ row }) => [
        <GridActionsCellItem
          key="edit"
          label="Edit"
          icon={<Edit htmlColor={extraColors.white} />}
          showInMenu
          onClick={() => {
            setEditRow(row)
            setAssetModalOpen(true)
          }}
        />,
        <GridActionsCellItem
          key="delete-location"
          label="Delete Location"
          css={{
            "&:hover": {
              backgroundColor: extraColors.status.redMuted,
            },
          }}
          icon={<Delete htmlColor={extraColors.status.redLight} />}
          showInMenu
          onClick={() => setRemoveAssetId(row.asset_id ? [row.asset_id] : [])}
        />,
      ],
    },
  ])
}
